﻿<template>
  <transition v-cloak name="modal">
    <v-modal :is-open="showModal" data-test-mit-id-modal @close="closeClick">
      <template #title>
        <h2 class="center" v-html="modalTitle" />
      </template>
      <template #content>
        <p class="center" v-html="modalText" />
      </template>
      <template #buttons>
        <div>
          <div class="row">
            <div v-if="!mitIdSuccess" class="medium-6 columns">
              <button
                v-if="stepOne"
                class="button secondary text-center"
                @click="stageTwo"
                v-html="skipButtonText"
              />
              <button
                v-if="stepTwo"
                class="button secondary text-center"
                @click="stageOne"
                v-html="vueModel.BackButtonLabel"
              />
              <button
                v-if="mitIdError"
                class="button secondary text-center"
                @click="stageOne"
                v-html="vueModel.BackButtonLabel"
              />
            </div>
            <div :class="['medium-6 columns', { 'centered-column': mitIdSuccess }]">
              <popup-window
                v-if="stepOne"
                :url="mitIdSignUrl"
                :title="mitIdPopupTitle"
                :width="980"
                :height="1040"
                :button-label="vueModel.MitIdButtonLabel"
                @popup-closed="popupWindowClosed()"
              />
              <button
                v-if="stepTwo"
                class="button text-center"
                @click="skipProposal"
                v-html="vueModel.SkipButtonLabel"
              />
              <button
                v-if="mitIdError"
                class="button text-center"
                @click="skipProposal"
                v-html="skipButtonText"
              />
              <button
                v-if="mitIdSuccess"
                class="button text-center"
                @click="finish"
                v-html="vueModel.CloseButtonLabel"
              />
            </div>
          </div>
        </div>
      </template>
    </v-modal>
  </transition>
</template>

<script lang="ts">
import PopupWindow from './PopupWindow.vue';
import { MitIDModalContentModel } from './interfaces/MitIDModalContentModel';

import store from '@/store';
import {
  BasketProposal,
  ProposalBasketStoreState
} from '@/store/modules/interfaces/ProposalBasketStoreState';
import { defineComponent, PropType } from 'vue';
import { mapState } from 'vuex';
import axios from 'axios';

export default defineComponent({
  name: 'MitIdModal',
  components: {
    PopupWindow
  },
  props: {
    showModal: {
      type: Boolean,
      default() {
        return false;
      }
    },
    content: {
      type: Object as PropType<MitIDModalContentModel>,
      default() {
        return {} as MitIDModalContentModel;
      }
    }
  },
  data() {
    return {
      vueModel: this.content,
      stepOne: true,
      stepTwo: false,
      mitIdSuccess: false,
      mitIdError: false,
      memberSigned: false
    };
  },
  computed: {
    ...mapState('proposalBasket', {
      proposals: (state: any) => (state as ProposalBasketStoreState).proposals,
      selectedProposals: (state: any) => (state as ProposalBasketStoreState).selectedProposals,
      index: (state: any) => (state as ProposalBasketStoreState).index
    }),
    skipButtonText(): string {
      if (this.selectedProposals.length === 1) {
        return this.vueModel.BasketButtonLink.Text;
      }

      return this.vueModel.SkipButtonLabel;
    },
    modalTitle(): string {
      return this.stepOne
        ? this.vueModel.MainTitle
        : this.stepTwo
          ? this.vueModel.SkipTitle
          : this.GetStatusTitle();
    },
    modalText(): string {
      return this.stepOne
        ? this.vueModel.MainText
        : this.stepTwo
          ? this.vueModel.SkipText
          : this.GetStatusText();
    },
    identifier(): string {
      let proposalIdentifier = this.selectedProposals[this.index];
      let proposal = this.proposals.find(
        (pro: BasketProposal) => pro.id == proposalIdentifier
      ) as BasketProposal;
      return proposal.externalProposalNumber;
    },
    proposalName(): string {
      let proposalIdentifier = this.selectedProposals[this.index];
      let proposal = this.proposals.find(
        (pro: BasketProposal) => pro.id == proposalIdentifier
      ) as BasketProposal;
      return proposal.name;
    },
    mitIdSignUrl(): string {
      return `${this.vueModel.ApiLink.Url}&identifier=${this.identifier}&insuranceName=${this.proposalName}`;
    },
    mitIdVerifyUrl(): string {
      return `${this.vueModel.CacheLink.Url}?externalProposalNumber=${this.identifier}`;
    },
    mitIdPopupTitle(): string {
      return 'MitIdSignPoppup';
    }
  },
  store,
  methods: {
    closeClick() {
      if (this.memberSigned) {
        this.$emit('hide-mitid-modal');
      } else if (this.mitIdSuccess) {
        this.$emit('mitid-complete');
      } else {
        this.$emit('hide-mitid-modal');
      }
      this.stageOne();
    },
    finish() {
      if (this.memberSigned) {
        this.$emit('hide-mitid-modal');
      }

      this.$emit('mitid-complete');
      this.stageOne();
    },
    stageOne() {
      this.stepOne = true;
      this.stepTwo = false;
      this.mitIdSuccess = false;
      this.mitIdError = false;
    },
    stageTwo() {
      if (this.selectedProposals.length === 1) {
        location.href = this.vueModel.BasketButtonLink.Url;
        return;
      }
      this.stepOne = false;
      this.stepTwo = true;
      this.mitIdSuccess = false;
      this.mitIdError = false;
    },
    skipProposal() {
      if (this.selectedProposals.length === 1) {
        location.href = this.vueModel.BasketButtonLink.Url;
        return;
      }

      this.closeClick();
      const index = this.index;
      setTimeout(() => {
        this.selectedProposals.splice(index, 1);
        this.$store.commit('proposalBasket/setSelectedProposals', this.selectedProposals);
        this.$store.commit('proposalBasket/setIndex', 100);
      }, 100);
      setTimeout(() => {
        this.$store.commit('proposalBasket/setIndex', index);
      }, 200);
    },
    setMitIdSigned(value: boolean, signedByMember: boolean) {
      this.mitIdSuccess = value && !signedByMember;
      this.mitIdError = !this.mitIdSuccess;
      this.stepOne = false;
      this.stepTwo = false;
      this.memberSigned = signedByMember;
    },
    popupWindowClosed() {
      axios.get(this.mitIdVerifyUrl).then((response) => {
        if (response.data?.Signed) {
          this.setMitIdSigned(true, response.data.SignedByMember);
          this.$emit('mitid-signed', response.data?.Name);
        } else {
          this.setMitIdSigned(false, false);
        }
      });
    },
    GetStatusTitle(): string {
      return this.memberSigned
        ? this.vueModel.WrongSignatureTitle
        : this.mitIdSuccess
          ? this.vueModel.SuccessTitle
          : this.mitIdError
            ? this.vueModel.ErrorTitle
            : '';
    },
    GetStatusText(): string {
      return this.memberSigned
        ? this.vueModel.WrongSignatureText
        : this.mitIdSuccess
          ? this.vueModel.SuccessText
          : this.mitIdError
            ? this.vueModel.ErrorText
            : '';
    }
  }
});
</script>

<style lang="scss" scoped>
.center {
  text-align: center;
}

.centered-column {
  margin: 0 auto;
}
</style>
